import { ApprovalState, ContentChannel, isPostFailed } from '@mg/shared';
import type { Post, Tenant } from '@mg/shared';
import { editPagePath } from '../brand/paths';

/**
 * TEST MODE — a workspace with auto-publishing switched off.
 *
 * What it actually means, from `post-release.service.ts`: the release worker selects
 * `tenant.autoPublishEnabled === true`, so a workspace with it off is skipped ENTIRELY. A scheduled
 * post there is never picked up, never delivered and never errors — it simply sits past its time
 * until `isPostFailed` calls it failed on lateness alone. Manual "Publish now" is unaffected.
 *
 * That is a sensible default (nothing posts to a live account on a timer until someone opts in) with
 * one bad property: it is silent. The workspace looks like it is scheduling, and the first sign
 * otherwise is a post in the Failed tab with no reason attached — because there is no `publishError`
 * to show. These helpers are what put the reason back.
 */

/** The id of the Approvals section on Edit Brand Workspace, and the deep link to it. */
export const APPROVALS_ANCHOR = 'approvals';
export const APPROVALS_LINK = `/workspace/edit#${APPROVALS_ANCHOR}`;
/**
 * With Brand Workspace v2 the Approvals section is on the Workflow page (web.md §2.6). The v2 Edit route
 * also sends APPROVALS_LINK here, for old links and bookmarks (features/brand-routes/brandRoutes.tsx).
 */
export const APPROVALS_LINK_V2 = `/workspace/edit/workflow#${APPROVALS_ANCHOR}`;

/**
 * APPROVALS_LINK_V2 for one workspace: `/workspace/edit/workflow?ws=<slug>#approvals`. The `?ws` keeps the
 * hash: on a link without one, TenantProvider adds it by replacing the URL with its query alone, which
 * drops the hash, and the page would open at its top instead of at Approvals.
 */
export function approvalsLinkV2(slug: string): string {
  return `${editPagePath('workflow', slug)}#${APPROVALS_ANCHOR}`;
}

/** Whether this workspace has scheduled posting to live platforms switched off. */
export function isTestMode(tenant: Pick<Tenant, 'autoPublishEnabled'> | null | undefined): boolean {
  return tenant != null && !tenant.autoPublishEnabled;
}

/**
 * Whether a post failed BECAUSE the workspace is in test mode, rather than for a reason of its own.
 *
 * Every condition here is doing work:
 *  · test mode      — with auto-publishing on, the worker did run, so a failure is a real one.
 *  · no publishError — a recorded error means delivery WAS attempted and something else went wrong;
 *                     test mode never attempts delivery, so it never leaves one behind.
 *  · SOCIAL         — the only channel the worker publishes. Blog and video are an n8n handoff, and
 *                     a blog EXPORT post is advanced by `advancePlatformScheduled`, which runs
 *                     whatever this flag says. Their lateness is not test mode's doing.
 *  · released       — a draft or in-review post carries a PROPOSED time, and `isPostFailed` counts
 *                     that as failed once it passes. Nothing would have published it either way, so
 *                     blaming test mode would send someone to fix a setting that isn't the problem.
 *
 * `state` is the post's released state as the caller already computes it for the badge
 * (`postDisplayState` on a card, `postBadgeState` in a list) — the approval-mode rules stay in one
 * place rather than being restated here.
 */
export function isTestModeFailure(
  post: Post,
  state: ApprovalState,
  tenant: Pick<Tenant, 'autoPublishEnabled'> | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!isTestMode(tenant)) return false;
  if (post.publishError) return false;
  if (post.channel !== ContentChannel.SOCIAL) return false;
  if (state !== ApprovalState.SCHEDULED) return false;
  return isPostFailed(post, now);
}

/** Scroll the Approvals section into view, when it is on the page already. */
export function scrollToApprovals(): void {
  document.getElementById(APPROVALS_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
